import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  LIMITS, TYPES, RSI_INTERVALS, typeAllowed, presetAllowed, telegramAllowed, LEVEL_LABEL, listAlerts, listEvents, createAlerts, updateAlert, deleteAlert,
  telegramStatus, telegramUnlink, requestTelegramLink, alertErrorMessage, RSI_TYPE_TEXT, describeAlert, fmtLevel,
  resolveCoin, priceOf, initialState, directionFor, markSeen, listMutes, deleteMute, listPresets, applyPreset,
} from '../utils/alerts';
import { trackEvent } from '../utils/track';
import { openLanding } from '../utils/landing';
import { PRO_PRICE_TEXT, PLUSZ_PRICE_TEXT, PLUSZ_PATH } from '../utils/pricing';
import { fetchLinearTickers } from '../utils/bybit';
import { fmtPct, fmtDateTime, fmtClock } from '../utils/fmt';
import './AlertsPanel.css';

/* ══════════════════════════════════════════════════════════════════════════
   Riasztások panel (2b)
   Létrehozás piszkozatból (coin-fejléc, Trade Tervező, RSI-áttekintés / Funding /
   Derivatívák sorai) vagy kézzel; lista, szüneteltetés, törlés, előzmény,
   Telegram-összekötés. A korlátokat a szerver érvényesíti — itt csak előre
   jelezzük őket, egy mondatos PRO-ajánlattal (nincs sötét minta: a FREE
   lehetőség mindig ott marad, és semmi nincs előre bepipálva).
   ══════════════════════════════════════════════════════════════════════════ */

const STATUS_LABEL = {
  active: 'Aktív',
  paused: 'Szüneteltetve',
  paused_limit: 'Szünetel (FREE-korlát)',
  triggered: 'Kioldott',
};

const EVENT_NOTE = {
  rate_limited: 'nem ment ki: óránkénti korlát (20 üzenet)',
  email_suppressed: 'nem ment ki: leiratkoztál a leveleinkről',
  telegram_not_linked: 'Telegram nincs összekötve',
  telegram_paused_free: 'Telegram FREE szinten szünetel – e-mailben ment',
  summary: 'összesítő üzenetben',
  deferred: 'az óránkénti 20 fölött – az óránkénti összesítőben',
  digest: 'az óránkénti összesítőben ment ki',
};

const FREE_NOTE = 'FREE szinten a riasztásokat e-mailben kapod. Telegramon Plusszal vagy PRO-val.';

const roundLevel = (p) => {
  if (!(p > 0)) return '';
  const d = p >= 1000 ? 0 : p >= 100 ? 2 : p >= 1 ? 4 : 8;
  return String(Number(p.toFixed(d)));
};

// Bybit linear USDT perpetuals — the symbols the server accepts (alerts_guard).
const SYMBOL_RE = /^[A-Z0-9]{2,30}USDT$/;
const baseOf = (sym) => sym.replace(/USDT$/, '');

/* Distance of the typed level from the current price, e.g. "+0,28%". */
function levelDistance(level, price) {
  const l = Number(String(level).replace(',', '.'));
  if (!(l > 0) || !(price > 0)) return null;
  const pct = (l / price - 1) * 100;
  return fmtPct(pct, Math.abs(pct) < 1 ? 2 : 1);
}

/* Coin picker with search over Bybit linear USDT perpetuals. */
function CoinPicker({ symbol, onPick }) {
  const [all, setAll] = useState(null);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let cancelled = false;
    fetchLinearTickers()
      .then(map => { if (!cancelled) setAll(Object.keys(map).filter(k => SYMBOL_RE.test(k)).sort()); })
      .catch(() => { if (!cancelled) setAll([]); });
    return () => { cancelled = true; };
  }, []);
  const matches = useMemo(() => {
    if (!all) return [];
    const t = q.trim().toUpperCase().replace(/USDT$/, '');
    if (!t) return [];
    const exact = [], starts = [], contains = [];
    for (const sym of all) {
      const b = baseOf(sym);
      if (b === t || b === `1000${t}` || b === `1000000${t}` || b === `${t}1000`) exact.push(sym);
      else if (b.startsWith(t)) starts.push(sym);
      else if (b.includes(t)) contains.push(sym);
    }
    return [...exact, ...starts, ...contains].slice(0, 8);
  }, [all, q]);
  const pick = (sym) => { setQ(''); setOpen(false); onPick(sym); };
  return (
    <label className="alp-wide alp-picker">Coin
      <input
        id="alp-coin"
        role="combobox"
        aria-expanded={open && matches.length > 0}
        aria-controls="alp-coin-list"
        autoComplete="off"
        placeholder={symbol ? `${baseOf(symbol)} — keress másik coint…` : 'Keress coint (pl. ETH, HYPE)…'}
        value={q}
        onChange={e => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={e => { if (e.key === 'Enter' && matches[0]) { e.preventDefault(); pick(matches[0]); } }}
      />
      {open && matches.length > 0 && (
        <ul id="alp-coin-list" className="alp-picker-list" role="listbox">
          {matches.map(sym => (
            <li key={sym} role="option" aria-selected={sym === symbol}>
              <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => pick(sym)}>
                <strong>{baseOf(sym)}</strong><span>{sym}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && q.trim() && all && matches.length === 0 && <span className="alp-hint">Nincs ilyen Bybit USDT perpetual.</span>}
    </label>
  );
}

const timeHu = (iso) => fmtDateTime(iso);
const snoozedUntil = (a) => a.snoozed_until && new Date(a.snoozed_until).getTime() > Date.now();

/* Draft (from a button somewhere in the terminal) → editable form. */
async function buildForm(draft, tgOk, tgLinked) {
  const base = {
    kind: draft.kind || 'price', source: draft.source || 'panel', symbol: '', coinLabel: '',
    mult: 1, price: null, error: null,
    channels: { email: true, telegram: !!(tgOk && tgLinked) },
    repeat: 'once', cooldown: 60, note: '',
  };
  if (draft.kind === 'price' || draft.kind === 'plan') {
    const r = draft.coin ? await resolveCoin(draft.coin).catch(() => null) : null;
    if (!r) return { ...base, error: 'Ehhez a coinhoz nincs Bybit USDT perpetual, ezért nem állítható be rá riasztás.' };
    const f = { ...base, symbol: r.symbol, mult: r.mult, price: r.price, coinLabel: (draft.coin.symbol || '').toUpperCase() };
    if (draft.kind === 'price') {
      const level = roundLevel(r.price);
      return { ...f, level, direction: 'up' };
    }
    const rows = [
      { plan_label: 'Belépő', level: draft.entry },
      { plan_label: 'Stop', level: draft.sl },
      { plan_label: 'Cél', level: draft.tp },
    ].filter(x => Number(x.level) > 0).map(x => ({ ...x, level: String(x.level), direction: directionFor(Number(x.level), r.price), on: true }));
    return { ...f, name: draft.name || `${f.coinLabel} ${draft.direction === 'short' ? 'short' : 'long'}`, rows };
  }
  const { mult, price } = await priceOf(draft.symbol).catch(() => ({ mult: 1, price: null }));
  const f = { ...base, symbol: draft.symbol, mult, price, coinLabel: draft.symbol };
  if (draft.kind === 'rsi') {
    const low = draft.rsi != null ? draft.rsi < 50 : true;
    return { ...f, interval: draft.interval || 'D', threshold: low ? '30' : '70', side: low ? 'below' : 'above' };
  }
  if (draft.kind === 'funding') return { ...f, threshold: '0.05' };
  if (draft.kind === 'oi') return { ...f, window_h: '4', pct: '5' };
  return f;
}

const NOTE_MAX = 140;
const PRESET_ERROR_REASON = { ALERT_LIMIT: 'limit', ALERT_TYPE_PRO: 'type', ALERT_CHANNEL_PRO: 'telegram' };

const KIND_TYPE = { price: 'price_cross', plan: 'plan_level', rsi: 'rsi_zone', funding: 'funding_extreme', oi: 'oi_change' };

/* Form → rows for the alerts table. */
function rowsFromForm(f) {
  const channels = [f.channels.telegram && 'telegram', f.channels.email && 'email'].filter(Boolean);
  const common = { symbol: f.symbol, channels, repeat: f.repeat, cooldown_minutes: Number(f.cooldown) || 60 };
  // `note` only when filled in, so inserts keep working before the 2d migration.
  const note = (f.note || '').trim().slice(0, NOTE_MAX);
  if (note) common.note = note;
  const level = (v) => Number(String(v).replace(',', '.'));
  if (f.kind === 'price') {
    const lv = level(f.level);
    return [{ ...common, type: 'price_cross', params: { level: lv, direction: f.direction, mult: f.mult }, last_state: initialState(f.direction, lv, f.price) }];
  }
  if (f.kind === 'plan') {
    const ref = `plan:${Date.now()}`;
    return f.rows.filter(r => r.on).map(r => {
      const lv = level(r.level);
      return {
        ...common, type: 'plan_level', plan_ref: ref, label: r.plan_label,
        params: { level: lv, direction: r.direction, mult: f.mult, plan_label: r.plan_label, plan_name: f.name.slice(0, 80) },
        last_state: initialState(r.direction, lv, f.price),
      };
    });
  }
  if (f.kind === 'rsi') return [{ ...common, type: 'rsi_zone', params: { interval: f.interval, threshold: level(f.threshold), side: f.side } }];
  if (f.kind === 'funding') return [{ ...common, type: 'funding_extreme', params: { threshold: level(f.threshold) } }];
  return [{ ...common, type: 'oi_change', params: { window_h: f.window_h, pct: level(f.pct) } }];
}

/* A következő szint ajánlata. FREE-nek a Plusz (15 riasztás, RSI, Telegram),
   kivéve a funding- és OI-riasztást, ami csak PRO; Plusznak a PRO. */
function ProOffer({ user, placement, reason, level = 'free', type = null }) {
  const plusz = level === 'free' && !(reason === 'type' && type && !TYPES[type]?.plusz);
  const lead = level === 'plusz' ? {
    limit: 'A Plusz szinten egyszerre 15 riasztás lehet aktív.',
    type: 'A funding- és OI-riasztás PRO-funkció.',
    paused: '',
  }[reason] || '' : {
    limit: 'A FREE szinten egyszerre 3 riasztás lehet aktív.',
    type: plusz ? RSI_TYPE_TEXT : 'A funding- és OI-riasztás PRO-funkció.',
    telegram: 'A Telegram-értesítés a Plusszal nyílik; FREE szinten e-mailben szólunk.',
    paused: '',
  }[reason] || '';
  return (
    <div className="alp-offer" role="note">
      <span>{lead} {plusz
        ? `Plusszal 15 riasztás, RSI-riasztás és Telegram: ${PLUSZ_PRICE_TEXT}.`
        : `PRO-val 50 riasztás, minden típus és Telegram: ${PRO_PRICE_TEXT}.`}</span>
      <button type="button" className="alp-link" onClick={() => openLanding(plusz ? PLUSZ_PATH : '/pro', {
        user, event: plusz ? 'plusz_cta_click' : 'pro_cta_clicked', eventProps: plusz ? { hely: `terminal_riasztas_${placement}` } : { source: 'alerts', placement },
      })}>{plusz ? 'Plusszal nyílik →' : 'PRO-val nyílik →'}</button>
    </div>
  );
}

export default function AlertsPanel({ open, onClose, user, isPro, level: levelProp, draft, onDraftDone, defaultCoin, onSeen }) {
  const level = levelProp || (isPro ? 'pro' : 'free');
  const limit = LIMITS[level];
  const tgOk = telegramAllowed(level);
  const [alerts, setAlerts] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [tg, setTg] = useState({ linked: false });
  const [tgBusy, setTgBusy] = useState(false);
  const [tgError, setTgError] = useState(null);
  const [tgWaiting, setTgWaiting] = useState(false);
  const [tab, setTab] = useState('list');
  const [form, setForm] = useState(null);
  const [formBusy, setFormBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [mutes, setMutes] = useState([]);
  const [presets, setPresets] = useState([]);
  const [presetBusy, setPresetBusy] = useState(null);
  // { id, code, text } — error or PRO offer under one preset row
  const [presetMsg, setPresetMsg] = useState(null);
  const pollRef = useRef(null);

  const reload = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true); setLoadError(null);
    try {
      const [a, e, t, m] = await Promise.all([
        listAlerts(), listEvents(), telegramStatus().catch(() => ({ linked: false })), listMutes(),
      ]);
      setAlerts(a); setEvents(e); setTg(t); setMutes(m);
    } catch (err) {
      setLoadError('A riasztásokat most nem sikerült betölteni.');
    } finally { setLoading(false); }
  }, [user?.id]);

  useEffect(() => {
    if (!open || !user?.id) return undefined;
    let cancelled = false;
    listPresets().then(p => { if (!cancelled) setPresets(p); });
    return () => { cancelled = true; };
  }, [open, user?.id]);

  useEffect(() => {
    if (!open) return;
    reload();
    if (user?.id) { markSeen(user.id); onSeen?.(); }
  }, [open, reload, user?.id, onSeen]);

  // Draft from elsewhere in the terminal → the create form.
  useEffect(() => {
    if (!open || !draft) return;
    let cancelled = false;
    setFormBusy(true); setSaveError(null); setTab('list');
    buildForm(draft, tgOk, tg.linked).then(f => { if (!cancelled) setForm(f); }).finally(() => {
      if (!cancelled) { setFormBusy(false); onDraftDone?.(); }
    });
    return () => { cancelled = true; };
  }, [open, draft]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const active = alerts.filter(a => a.status === 'active');
  const mutedSymbols = useMemo(() => new Set(mutes.map(m => m.symbol)), [mutes]);
  const pausedLimit = alerts.filter(a => a.status === 'paused_limit');
  const groups = useMemo(() => ([
    ['Aktív', alerts.filter(a => a.status === 'active')],
    ['Szünetel', alerts.filter(a => a.status === 'paused' || a.status === 'paused_limit')],
    ['Kioldott', alerts.filter(a => a.status === 'triggered')],
  ]), [alerts]);

  /* UX pre-check (the server decides): which offer, if any, applies to the form. */
  const formRows = form && !form.error ? rowsFromForm(form) : [];
  const blocker = !form || form.error ? null
    : !typeAllowed(level, KIND_TYPE[form.kind]) ? 'type'
    : !tgOk && form.channels.telegram ? 'telegram'
    : active.length + formRows.length > limit ? 'limit'
    : null;

  const set = (patch) => setForm(f => ({ ...f, ...patch }));

  // Another coin from the picker: new symbol, price and (price alert) level.
  const changeSymbol = async (sym) => {
    const { mult, price } = await priceOf(sym).catch(() => ({ mult: 1, price: null }));
    setForm(f => {
      if (!f) return f;
      const next = { ...f, symbol: sym, mult, price, coinLabel: baseOf(sym) };
      if (f.kind === 'price') { next.level = roundLevel(price); next.direction = 'up'; }
      return next;
    });
    trackEvent('alert_coin_changed', { kind: form?.kind, source: form?.source }, user);
  };
  const setRow = (i, patch) => setForm(f => ({ ...f, rows: f.rows.map((r, j) => (j === i ? { ...r, ...patch } : r)) }));

  const startNew = async () => {
    if (!defaultCoin) return;
    setFormBusy(true); setSaveError(null);
    setForm(await buildForm({ kind: 'price', coin: defaultCoin, source: 'panel' }, tgOk, tg.linked));
    setFormBusy(false);
  };

  const save = async () => {
    if (blocker === 'limit') trackEvent('alert_limit_hit', { level, source: form.source }, user);
    if (blocker || !formRows.length) return;
    if (!formRows.every(r => r.channels.length > 0)) { setSaveError('Válassz legalább egy csatornát.'); return; }
    setSaving(true); setSaveError(null);
    try {
      await createAlerts(formRows.map(r => ({ ...r, user_id: user.id })));
      formRows.forEach(r => trackEvent('alert_created', { type: r.type, source: form.source }, user));
      setForm(null);
      await reload();
    } catch (err) {
      const { code, text } = alertErrorMessage(err, KIND_TYPE[form.kind]);
      if (code === 'ALERT_LIMIT') trackEvent('alert_limit_hit', { level, source: form.source }, user);
      setSaveError({ code, text });
    } finally { setSaving(false); }
  };

  const act = async (a, patch) => {
    try { await updateAlert(a.id, patch); await reload(); } catch (err) {
      const { code, text } = alertErrorMessage(err, a.type);
      if (code === 'ALERT_LIMIT') trackEvent('alert_limit_hit', { level, source: 'resume' }, user);
      setLoadError(code ? { code, text } : text);
    }
  };
  const remove = async (a) => {
    setConfirmDel(null);
    try { await deleteAlert(a.id); await reload(); } catch { setLoadError('A törlés nem sikerült.'); }
  };

  const unmute = async (symbol) => {
    try { await deleteMute(user.id, symbol); setMutes(await listMutes()); } catch { setLoadError('Nem sikerült menteni. Próbáld újra.'); }
  };

  const setupPreset = async (p) => {
    setPresetMsg(null);
    // FREE: a PRO preset only shows the offer — the server would refuse it anyway
    if (!presetAllowed(level, p)) { setPresetMsg({ id: p.id, code: 'ALERT_TYPE_PRO' }); return; }
    setPresetBusy(p.id);
    try {
      await applyPreset(p.id);
      await reload();
    } catch (err) {
      const { code, text } = alertErrorMessage(err, p.plusz ? 'rsi_zone' : 'funding_extreme');
      if (code === 'ALERT_LIMIT') trackEvent('alert_limit_hit', { level, source: 'preset' }, user);
      setPresetMsg({ id: p.id, code, text });
    } finally { setPresetBusy(null); }
  };

  const connectTelegram = async () => {
    setTgError(null);
    // FREE: Telegram needs Plusz or PRO — say so right away instead of asking the server
    if (!tgOk) { setTgError('pro'); return; }
    // open the tab inside the click (popup blockers), navigate it once the link is there
    const w = window.open('', '_blank');
    setTgBusy(true);
    trackEvent('telegram_link_started', { source: 'alerts' }, user);
    const r = await requestTelegramLink();
    setTgBusy(false);
    if (!r.url) {
      if (w) w.close();
      setTgError(r.code === 'PRO_ONLY' ? 'pro' : r.error);
      return;
    }
    if (w) w.location.href = r.url; else window.location.href = r.url;
    // Wait for the bot's /start (the link is valid for 15 minutes).
    clearInterval(pollRef.current);
    setTgWaiting(true);
    const started = Date.now();
    pollRef.current = setInterval(async () => {
      const s = await telegramStatus().catch(() => null);
      if (s?.linked) {
        clearInterval(pollRef.current);
        setTgWaiting(false);
        setTg(s);
        trackEvent('telegram_linked', { source: 'alerts' }, user);
      } else if (Date.now() - started > 15 * 60e3) { clearInterval(pollRef.current); setTgWaiting(false); }
    }, 3000);
  };

  const disconnectTelegram = async () => {
    try { await telegramUnlink(); setTg({ linked: false }); } catch { setTgError('A leválasztás nem sikerült.'); }
  };

  if (!open) return null;

  return (
    <div className="alp-scrim" onClick={onClose}>
      <aside className="alp" role="dialog" aria-modal="true" aria-label="Riasztások" onClick={e => e.stopPropagation()}>
        <header className="alp-head">
          <div>
            <h2>Riasztások</h2>
            <div className="alp-quota">{active.length} / {limit} riasztás ({LEVEL_LABEL[level]})</div>
          </div>
          <button type="button" className="alp-x" onClick={onClose} aria-label="Bezárás">×</button>
        </header>

        {!tgOk && <div className="alp-free-note">{FREE_NOTE}</div>}

        {pausedLimit.length > 0 && level !== 'pro' && (
          <div className="alp-paused">
            <strong>{pausedLimit.length} riasztásod szünetel – {level === 'free' ? 'Plusszal vagy PRO-val' : 'PRO-val'} újraindulnak.</strong>
            <ProOffer user={user} placement="paused_banner" reason="paused" level={level} />
          </div>
        )}

        {/* Telegram */}
        <section className="alp-tg">
          <div className="alp-tg-row">
            <span className="alp-tg-ic" aria-hidden="true">✈</span>
            <div className="alp-tg-text">
              <div className="alp-tg-title">Telegram</div>
              <div className={`alp-tg-status ${tg.linked ? 'is-ok' : ''}`}>
                {tg.linked ? (tgOk ? 'Összekötve' : 'Összekötve – FREE szinten szünetel') : 'Nincs összekötve'}
              </div>
            </div>
            {tg.linked ? (
              <button type="button" className="alp-btn" onClick={disconnectTelegram}>Leválasztás</button>
            ) : (
              <button type="button" className="alp-btn" onClick={connectTelegram} disabled={tgBusy}>
                {tgBusy ? 'Link készül…' : 'Telegram összekötése'}
              </button>
            )}
          </div>
          {tgError === 'pro' ? <ProOffer user={user} placement="telegram_connect" reason="telegram" level={level} />
            : tgError ? <div className="alp-error">{tgError}</div> : null}
          {tgWaiting && !tg.linked && !tgError && !tgBusy && (
            <div className="alp-hint">A Telegramban nyomd meg a „Start” gombot — a panel magától frissül.</div>
          )}
          {/* 2c: TAG-kérés / előfizetés / próba-értesítések — a landing Fiók-blokkjában */}
          <button type="button" className="alp-link alp-tg-settings" onClick={() => openLanding('/dashboard?focus=telegram', {
            user, event: 'telegram_settings_opened', eventProps: { source: 'alerts' },
          })}>Értesítési beállítások →</button>
        </section>

        {/* Create */}
        {formBusy && <div className="alp-hint">Betöltés…</div>}
        {form && !formBusy && (
          <section className="alp-form" aria-label="Új riasztás">
            {form.error ? (
              <>
                <div className="alp-error">{form.error}</div>
                <button type="button" className="alp-link" onClick={() => setForm(null)}>Bezárás</button>
              </>
            ) : (
              <>
                <div className="alp-form-head">
                  <strong>{form.kind === 'plan' ? 'Riasztások a tervhez' : TYPES[KIND_TYPE[form.kind]].label}</strong>
                  <span className="alp-muted">{form.symbol}{form.price ? ` · most ${fmtLevel(form.price)} $` : ''}</span>
                </div>

                {form.kind !== 'plan' && <CoinPicker symbol={form.symbol} onPick={changeSymbol} />}

                {form.kind === 'price' && (
                  <div className="alp-grid">
                    <label>Szint ($)
                      <input id="alp-level" inputMode="decimal" value={form.level} onChange={e => set({ level: e.target.value })} />
                      {form.price > 0 && (
                        <span className="alp-hint alp-distance">
                          most {fmtLevel(form.price)} ${levelDistance(form.level, form.price) ? ` · ${levelDistance(form.level, form.price)}` : ''}
                        </span>
                      )}
                    </label>
                    <label>Irány
                      <select id="alp-direction" value={form.direction} onChange={e => set({ direction: e.target.value })}>
                        <option value="up">felfelé keresztezi</option>
                        <option value="down">lefelé keresztezi</option>
                      </select>
                    </label>
                  </div>
                )}

                {form.kind === 'plan' && (
                  <>
                    <label className="alp-wide">A terv neve
                      <input id="alp-plan-name" value={form.name} maxLength={80} onChange={e => set({ name: e.target.value })} />
                    </label>
                    <div className="alp-plan">
                      {form.rows.map((r, i) => (
                        <div key={r.plan_label} className={`alp-plan-row ${r.on ? '' : 'is-off'}`}>
                          <label className="alp-check">
                            <input type="checkbox" checked={r.on} onChange={e => setRow(i, { on: e.target.checked })} />
                            {r.plan_label}
                          </label>
                          <input aria-label={`${r.plan_label} szint`} inputMode="decimal" value={r.level} onChange={e => setRow(i, { level: e.target.value })} />
                          <select aria-label={`${r.plan_label} irány`} value={r.direction} onChange={e => setRow(i, { direction: e.target.value })}>
                            <option value="up">felfelé</option>
                            <option value="down">lefelé</option>
                          </select>
                        </div>
                      ))}
                      {form.rows.length === 0 && <div className="alp-hint">Adj meg belépőt, stopot vagy célt a tervben.</div>}
                    </div>
                  </>
                )}

                {form.kind === 'rsi' && (
                  <div className="alp-grid">
                    <label>Idősík
                      <select value={form.interval} onChange={e => set({ interval: e.target.value })}>
                        {RSI_INTERVALS.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
                      </select>
                    </label>
                    <label>Feltétel
                      <select value={form.side} onChange={e => set({ side: e.target.value })}>
                        <option value="below">RSI alá megy</option>
                        <option value="above">RSI fölé megy</option>
                      </select>
                    </label>
                    <label>Küszöb
                      <input inputMode="decimal" value={form.threshold} onChange={e => set({ threshold: e.target.value })} />
                    </label>
                  </div>
                )}
                {form.kind === 'funding' && (
                  <div className="alp-grid">
                    <label>|funding| legalább (%)
                      <input inputMode="decimal" value={form.threshold} onChange={e => set({ threshold: e.target.value })} />
                    </label>
                  </div>
                )}
                {form.kind === 'oi' && (
                  <div className="alp-grid">
                    <label>Időablak
                      <select value={form.window_h} onChange={e => set({ window_h: e.target.value })}>
                        <option value="1">1 óra</option>
                        <option value="4">4 óra</option>
                      </select>
                    </label>
                    <label>|változás| legalább (%)
                      <input inputMode="decimal" value={form.pct} onChange={e => set({ pct: e.target.value })} />
                    </label>
                  </div>
                )}

                <label className="alp-wide alp-note-input">Megjegyzés
                  <input
                    id="alp-note"
                    maxLength={NOTE_MAX}
                    placeholder="Miért állítod be? (nem kötelező, a riasztásban visszakapod)"
                    value={form.note || ''}
                    onChange={e => set({ note: e.target.value })}
                  />
                </label>

                <div className="alp-opts">
                  <span className="alp-muted">Értesítés:</span>
                  <label className="alp-check"><input type="checkbox" checked={form.channels.email} onChange={e => set({ channels: { ...form.channels, email: e.target.checked } })} /> E-mail</label>
                  <label className="alp-check"><input type="checkbox" checked={form.channels.telegram} onChange={e => set({ channels: { ...form.channels, telegram: e.target.checked } })} /> Telegram{tgOk && !tg.linked ? ' (előbb kösd össze)' : ''}</label>
                </div>
                {!tgOk && <div className="alp-free-note">{FREE_NOTE}</div>}
                <div className="alp-opts">
                  <span className="alp-muted">Ismétlés:</span>
                  <select aria-label="Ismétlés" value={form.repeat} onChange={e => set({ repeat: e.target.value })}>
                    <option value="once">Egyszeri (az első kioldás után kikapcsol)</option>
                    <option value="rearm">Ismétlődő</option>
                  </select>
                  {form.repeat === 'rearm' && (
                    <select aria-label="Szünet két riasztás között" value={form.cooldown} onChange={e => set({ cooldown: e.target.value })}>
                      <option value="15">15 perc szünettel</option>
                      <option value="60">1 óra szünettel</option>
                      <option value="240">4 óra szünettel</option>
                      <option value="1440">1 nap szünettel</option>
                    </select>
                  )}
                </div>

                {blocker && <ProOffer user={user} placement={`form_${blocker}`} reason={blocker} level={level} type={KIND_TYPE[form.kind]} />}
                {saveError && (saveError.code && ['ALERT_LIMIT', 'ALERT_TYPE_PRO', 'ALERT_CHANNEL_PRO'].includes(saveError.code) && level !== 'pro'
                  ? <ProOffer user={user} placement="server_limit" level={level} type={KIND_TYPE[form.kind]} reason={{ ALERT_LIMIT: 'limit', ALERT_TYPE_PRO: 'type', ALERT_CHANNEL_PRO: 'telegram' }[saveError.code]} />
                  : <div className="alp-error">{saveError.text || saveError}</div>)}

                <div className="alp-actions">
                  <button type="button" className="alp-btn-primary" onClick={save} disabled={saving || !!blocker || !formRows.length}>
                    {saving ? 'Mentés…' : formRows.length > 1 ? `${formRows.length} riasztás mentése` : 'Riasztás mentése'}
                  </button>
                  <button type="button" className="alp-link" onClick={() => setForm(null)}>Mégse</button>
                </div>
                <p className="alp-note">Tájékoztató riasztás, nem befektetési tanács. A szerver percenként ellenőrzi az árat; csak valódi keresztezésre szól.</p>
              </>
            )}
          </section>
        )}

        {/* Tabs */}
        <nav className="alp-tabs">
          <button type="button" className={tab === 'list' ? 'is-on' : ''} onClick={() => setTab('list')}>Riasztásaim</button>
          <button type="button" className={tab === 'history' ? 'is-on' : ''} onClick={() => setTab('history')}>Előzmény</button>
          {!form && defaultCoin && <button type="button" className="alp-new" onClick={startNew}>+ Új riasztás</button>}
        </nav>

        {loadError && (typeof loadError === 'object' && loadError.code === 'ALERT_LIMIT' && level !== 'pro'
          ? <ProOffer user={user} placement="resume_limit" reason="limit" level={level} />
          : <div className="alp-error">{loadError.text || loadError}</div>)}
        {loading && alerts.length === 0 && <div className="alp-hint">Betöltés…</div>}

        {tab === 'list' && (
          <div className="alp-lists">
            {alerts.length === 0 && !loading && (
              <div className="alp-empty">
                Még nincs riasztásod. Állíts be egyet a coin fejlécéből („🔔 Riasztás”), vagy a Trade Tervezőből egy tervhez.
              </div>
            )}
            {groups.map(([title, list]) => list.length > 0 && (
              <div key={title} className="alp-group">
                <div className="alp-group-title">{title} <span className="alp-muted">{list.length}</span></div>
                {list.map(a => (
                  <div key={a.id} className="alp-item">
                    <div className="alp-item-main">
                      <div className="alp-item-desc">{describeAlert(a)}</div>
                      {a.note && <div className="alp-item-note">{a.note}</div>}
                      <div className="alp-item-meta">
                        <span className={`alp-pill is-${a.status}`}>{STATUS_LABEL[a.status]}</span>
                        <span>{TYPES[a.type]?.label}</span>
                        <span>{a.channels.map(c => (c === 'email' ? 'e-mail' : 'Telegram')).join(' + ')}</span>
                        {a.repeat === 'rearm' && <span>újra élesedik</span>}
                        {a.last_triggered_at && <span>utoljára {timeHu(a.last_triggered_at)}</span>}
                        {snoozedUntil(a) && <span className="alp-snooze">szundi {fmtClock(a.snoozed_until, false)}-ig</span>}
                        {mutedSymbols.has(a.symbol) && <span className="alp-snooze">némítva</span>}
                      </div>
                    </div>
                    <div className="alp-item-actions">
                      {mutedSymbols.has(a.symbol) && <button type="button" className="alp-link" onClick={() => unmute(a.symbol)}>Némítás vége</button>}
                      {a.status === 'active' && <button type="button" className="alp-link" onClick={() => act(a, { status: 'paused' })}>Szüneteltetés</button>}
                      {a.channels.includes('email')
                        ? a.channels.length > 1 && <button type="button" className="alp-link is-quiet" onClick={() => act(a, { channels: a.channels.filter(c => c !== 'email') })}>E-mail ki</button>
                        : <button type="button" className="alp-link is-quiet" onClick={() => act(a, { channels: [...a.channels, 'email'] })}>E-mail be</button>}
                      {a.status !== 'active' && <button type="button" className="alp-link" onClick={() => act(a, { status: 'active' })}>{a.status === 'triggered' ? 'Újraélesítés' : 'Folytatás'}</button>}
                      {confirmDel === a.id ? (
                        <span className="alp-confirm">Törlöd? <button type="button" className="alp-link is-danger" onClick={() => remove(a)}>Igen</button> <button type="button" className="alp-link" onClick={() => setConfirmDel(null)}>Mégse</button></span>
                      ) : (
                        <button type="button" className="alp-link is-quiet" onClick={() => setConfirmDel(a.id)}>Törlés</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))}

            {presets.length > 0 && (
              <section className="alp-presets" aria-label="Kész csomagok">
                <div className="alp-group-title">Kész csomagok</div>
                {presets.map(p => {
                  const locked = !presetAllowed(level, p);
                  const msg = presetMsg?.id === p.id ? presetMsg : null;
                  const offer = msg && level !== 'pro' && PRESET_ERROR_REASON[msg.code];
                  return (
                    <div key={p.id} className="alp-preset">
                      <div className="alp-item">
                        <div className="alp-item-main">
                          <div className="alp-item-desc">{locked && <span aria-label={p.plusz ? 'Plusszal nyílik' : 'PRO'}>🔒 </span>}{p.name}</div>
                          <div className="alp-item-meta"><span>{p.desc}</span>{p.pro && <span>{p.plusz ? 'PLUSZ' : 'PRO'}</span>}</div>
                        </div>
                        <button type="button" className="alp-btn" onClick={() => setupPreset(p)} disabled={presetBusy === p.id}>
                          {presetBusy === p.id ? 'Beállítás…' : 'Beállítom'}
                        </button>
                      </div>
                      {offer ? <ProOffer user={user} placement="preset" reason={offer} level={level} type={p.plusz ? 'rsi_zone' : 'funding_extreme'} />
                        : msg ? <div className="alp-error">{msg.text}</div> : null}
                    </div>
                  );
                })}
              </section>
            )}
          </div>
        )}

        {tab === 'history' && (
          <div className="alp-lists">
            {events.length === 0 ? (
              <div className="alp-empty">Még egy riasztásod sem szólalt meg.</div>
            ) : events.map(ev => (
              <div key={ev.id} className="alp-item">
                <div className="alp-item-main">
                  <div className="alp-item-desc">{ev.message || 'Riasztás'}</div>
                  <div className="alp-item-meta">
                    <span>{timeHu(ev.triggered_at)}</span>
                    {ev.channels_sent?.length > 0 && <span>elküldve: {ev.channels_sent.map(c => (c === 'email' ? 'e-mail' : 'Telegram')).join(' + ')}</span>}
                    {(ev.error || '').split(';').map(s => s.trim()).filter(s => EVENT_NOTE[s]).map(s => <span key={s}>{EVENT_NOTE[s]}</span>)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </aside>
    </div>
  );
}
